import React, { useEffect } from "react"
import PropTypes from "prop-types"
import moment from "moment-timezone"
import _take from "lodash/take"
import _orderBy from "lodash/orderBy"
import isEmpty from "lodash/isEmpty"
import { fetchProviders } from "app/feeds/fetch-providers"
import { createFeed } from "app/feeds/create-feed"
import { useRequestReducer } from "hooks/use-requests.js"
import placeholder from "app/campaigns/newsletter_placeholder.png"
import { requestRenderer } from "../request-renderer"

export function NewsletterFeed({
  style,
  items,
  onChange,
  element_id,
  user,
  useChildElements,
  numberOfBroadcasts,
  feedOrder,
  radius,
  dateFontColor,
  dateFontSize,
  includeImage,
  includeSentDate,
  titleFontColor,
  titleFontSize
}) {
  const { addChild } = useChildElements("parent_element_id", element_id)

  const [feedData, dispatch] = useRequestReducer({ data: undefined })
  const { status, data: feed, error } = feedData

  const update = response => {
    if (response.items) {
      dispatch({ type: "RESOLVE", payload: response })
    } else {
      dispatch({ type: "EMPTY" })
    }
  }

  const createNewsletterFeed = async () => {
    try {
      dispatch({ type: "FETCH" })
      let feedRequest = createFeed(user.account_id)
      let result = await feedRequest({
        name: "Newsletter",
        provider_name: "Newsletter",
        type: "Newsletter"
      })
      update(result)
    } catch (err) {
      dispatch({ type: "REJECT", payload: err.message })
    }
  }

  useEffect(() => {
    const getProviders = async () => {
      try {
        dispatch({ type: "FETCH" })
        let fetch = fetchProviders(user.account_id)
        let response = await fetch("Newsletter")
        if (response.providers.length) {
          return update(response.providers[0].feed)
        } else {
          return createNewsletterFeed()
        }
      } catch (err) {
        dispatch({ type: "EMPTY", payload: err.message })
      }
    }
    getProviders()
  }, [])

  useEffect(() => {
    if (status !== "success") {
      return
    }

    const children = feed.items
      .filter(item => {
        return !items.some(existing => {
          const campaign_id =
            item.properties.version === 2 || item.properties.campaign_id
              ? item.properties.campaign_id
              : item.properties.id
          return (
            existing.campaign_id === campaign_id ||
            existing.post_id === item.properties.post_id
          )
        })
      })
      .map(item => {
        item.version = item.properties.version
        item.campaign_id =
          item.properties.version === 2
            ? item.properties.campaign_id
            : item.properties.id
        item.post_id = item.properties.post_id
        item.type = "NewsletterItem"
        item.link = `/posts/${item.properties.slug}`
        item.thumbnail_url = item.properties.thumbnail_url
          ? item.properties.thumbnail_url
          : null
        item.thumbnail_alt = item.properties.thumbnail_alt
          ? item.properties.thumbnail_alt
          : null
        return item
      })

    if (!isEmpty(children)) {
      addChild(children, 0)
      onChange({
        feedId: feed.id
      })
    }
  }, [feed, status])

  const styles = {
    ...style
  }

  const imageStyles = {
    borderRadius: radius,
    maxWidth: 200,
    width: "100%",
    objectFit: "cover",
    maxHeight: 130
  }

  const titleStyles = {
    color: titleFontColor,
    fontSize: titleFontSize,
    marginBottom: "0.5rem"
  }

  const dateStyles = {
    color: dateFontColor,
    fontSize: dateFontSize
  }

  return requestRenderer(
    status,
    { error },
    {
      idle: () => null,
      empty: () => (
        <div className="text-center border-gray-300 border-solid border-4 p-5 bg-gray-50 rounded-lg">
          <h2
            style={{
              color: "#3d3d3d",
              fontWeight: "600",
              fontSize: "28px",
              marginBottom: "20px"
            }}
          >
            No public broadcasts yet
          </h2>
          <p className="text-gray-700" style={{ marginBottom: 0 }}>
            Add emails to your feed by selecting “Enable on public feeds” when
            creating or editing a broadcast.
          </p>
        </div>
      ),
      success: () => {
        const sortedArray = _orderBy(items, child => moment(child.timestamp), [
          feedOrder
        ])

        return (
          <div className="newsletter-feed" style={styles}>
            {_take(sortedArray, numberOfBroadcasts).map(item => {
              let date = moment(item.timestamp).fromNow()
              let thumbnailUrl = item.thumbnail_url
                ? item.thumbnail_url
                : placeholder
              let thumbnailAlt = item.thumbnail_alt
                ? item.thumbnail_alt
                : "thumbnail placeholder"

              return (
                <div
                  key={item.element_id}
                  className="flex flex-col md:flex-row items-center mb-6"
                >
                  {includeImage && (
                    <img
                      style={imageStyles}
                      src={thumbnailUrl}
                      alt={thumbnailAlt}
                      className="md:mr-8"
                    />
                  )}

                  <div className="text-left">
                    <h3 style={titleStyles}>{item.title}</h3>
                    {includeSentDate && <p style={dateStyles}>Sent {date}</p>}
                  </div>
                </div>
              )
            })}
          </div>
        )
      }
    }
  )()
}

NewsletterFeed.propTypes = {
  style: PropTypes.object.isRequired,
  items: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  element_id: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
  useChildElements: PropTypes.func.isRequired,
  numberOfBroadcasts: PropTypes.number.isRequired,
  radius: PropTypes.string.isRequired,
  dateFontColor: PropTypes.string.isRequired,
  dateFontSize: PropTypes.number.isRequired,
  includeImage: PropTypes.bool.isRequired,
  includeSentDate: PropTypes.bool.isRequired,
  titleFontColor: PropTypes.string.isRequired,
  titleFontSize: PropTypes.number.isRequired
}
